import React, { useState, useEffect } from 'react';
import { basisFromCase, DATA_BASIS_LETTER, DATA_BASIS_TITLE, type DataBasis } from '../utils/dataBasis';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import API from '../services/api';
import { fetchAllUsers } from '../services/usersApi';
import { getViolationColor, REJECTION_GROUNDS } from '../types/caseForm';
import { PlusIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, FunnelIcon, XMarkIcon, CheckBadgeIcon, GlobeAltIcon, ExclamationTriangleIcon, ShieldExclamationIcon, MapPinIcon,StarIcon, UserIcon, DocumentTextIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import ExportModal, { ExportFilters } from '../components/ExportModal';
import ImportModal from '../components/ImportModal';
import useCountries from '../hooks/useCountries';
import { formatCaseDate } from '../utils/formatDateValue';
import { isEndState as isDecided } from '../utils/yesNo';
import { formatDistanceToNow } from 'date-fns';
import { enumLabel } from '../utils/enumLabel';

interface Case {
  id: string;
  caseNumber: string;
  incidentDate: string;
  reportedDate: string;
  country: string;
  region: string;
  indigenousGroup: string;
  violationTypes: string[];
  otherViolationTypes: string[];
  status: string;
  priority: number;
  isPrimaryData: boolean;
  dataBasis?: DataBasis;
  documentorName?: string;
  isAutoSourced?: boolean;
  updatedAt?: string;
  aiDecision?: 'VALIDATED' | 'REJECTED' | 'NEEDS_REVIEW' | null;
  aiConfidence?: number | null;
  openConflicts?: number;
  unconfirmedAi?: number;
  pendingDuplicate?: boolean;
  submittedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
    profilePicture?: string | null;
  };
}

// Clickable column header with a direction indicator; the button carries aria-sort for screen readers.
const SortHeader: React.FC<{ label: string; sortKey: string; active: string; order: 'asc' | 'desc'; onSort: (k: any) => void; className?: string }> = ({ label, sortKey, active, order, onSort, className }) => {
  const isActive = active === sortKey;
  return (
    <th
      scope="col"
      aria-sort={isActive ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider ${className ?? ''}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900 dark:hover:text-gray-100 ${isActive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}
      >
        {label}
        <span aria-hidden="true" className={`text-[10px] ${isActive ? '' : 'opacity-30'}`}>{isActive && order === 'asc' ? '▲' : '▼'}</span>
      </button>
    </th>
  );
};

// Workflow rules, hand-mirrored from ipri-backend/src/controllers/caseController.js
// (VALID_TRANSITIONS / STATUS_ROLE_PERMISSIONS). The backend enforces them; the list only offers
// what would be accepted for every selected case.
const VALID_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['FOR_VALIDATION'],
  FOR_VALIDATION: ['IN_RESEARCH', 'REJECTED'],
  IN_RESEARCH: ['FOR_APPROVAL', 'REJECTED'],
  FOR_APPROVAL: ['APPROVED', 'REJECTED', 'FOR_VALIDATION'],
  APPROVED: ['IN_RESEARCH'],
  REJECTED: ['IN_RESEARCH'],
};
const STATUS_ROLE_PERMISSIONS: Record<string, string[]> = {
  FOR_APPROVAL: ['RESEARCHER', 'APPROVER', 'ADMIN'],
  APPROVED: ['APPROVER', 'ADMIN'],
  REJECTED: ['APPROVER', 'ADMIN'],
  IN_RESEARCH: ['RESEARCHER', 'APPROVER', 'ADMIN'],
  FOR_VALIDATION: ['DOCUMENTER', 'RESEARCHER', 'APPROVER', 'ADMIN'],
};

const STATUS_LABEL: Record<string, string> = { DRAFT: 'Draft', FOR_VALIDATION: 'For Validation', IN_RESEARCH: 'In Research', FOR_APPROVAL: 'For Approval', APPROVED: 'Approved', REJECTED: 'Rejected', ARCHIVED: 'Archived' };

const FilterChip: React.FC<{ label: string; onClear: () => void }> = ({ label, onClear }) => (
  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
    {label}
    <button
      type="button"
      onClick={onClear}
      className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-emerald-200"
      aria-label={`Remove ${label} filter`}
    >
      <XMarkIcon className="h-3 w-3" />
    </button>
  </span>
);

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const CASE_FILTERS_STORAGE_KEY = 'caseListFilters';

// Filters given in the URL (shareable links, dashboard queues). Repeatable keys may also be
// comma-separated. When any filter is present in the URL it wins over the session-saved state,
// and the view defaults to "all" unless `view=my` is given, so a link shows exactly what it says.
type DataSourceFilter = 'all' | 'primary' | 'secondary' | 'mixed' | 'auto';
const DATA_SOURCE_LABEL: Record<DataSourceFilter, string> = { all: 'All', primary: 'Primary', secondary: 'Secondary', mixed: 'Mixed', auto: 'AI-sourced' };
const URL_FILTER_KEYS = ['status', 'assignedTo', 'country', 'ipriRegion', 'search', 'priority', 'year', 'violationType', 'otherViolationType', 'dataSource', 'ldsf', 'view', 'page', 'sort', 'attention'];
const SORT_KEYS = ['caseNumber', 'reportedDate', 'createdAt', 'updatedAt', 'status', 'priority', 'country', 'indigenousGroup', 'documentorName', 'assignee'] as const;
type SortKey = typeof SORT_KEYS[number];
const parseUrlFilters = (sp: URLSearchParams): Record<string, any> => {
  if (!URL_FILTER_KEYS.some((k) => sp.has(k))) return {};
  const list = (k: string) => sp.getAll(k).flatMap((v) => v.split(',')).map((v) => v.trim()).filter(Boolean);
  const out: Record<string, any> = { viewMode: sp.get('view') === 'my' ? 'my' : 'all', currentPage: 1 };
  if (sp.has('status')) out.filterStatus = list('status');
  if (sp.has('assignedTo')) out.filterAssignment = sp.get('assignedTo');
  if (sp.has('country')) out.filterCountries = list('country');
  if (sp.has('ipriRegion')) out.filterIpriRegion = list('ipriRegion');
  if (sp.has('search')) out.searchTerm = sp.get('search') || '';
  if (sp.has('priority')) out.filterPriority = sp.get('priority');
  if (sp.has('year')) out.filterYear = sp.get('year') || '';
  if (sp.has('violationType')) out.filterViolationTypes = list('violationType');
  if (sp.has('otherViolationType')) out.filterOtherViolationTypes = list('otherViolationType');
  if (sp.has('dataSource')) out.filterDataSource = sp.get('dataSource');
  if (sp.has('ldsf')) out.filterIsLDSFApplicant = sp.get('ldsf');
  if (sp.has('page')) out.currentPage = Math.max(1, parseInt(sp.get('page') || '1', 10) || 1);
  if (sp.has('attention')) out.filterAttention = sp.get('attention') === '1';
  if (sp.has('sort')) {
    const [k, d] = (sp.get('sort') || '').split(':');
    if ((SORT_KEYS as readonly string[]).includes(k)) { out.sortBy = k; out.sortOrder = d === 'asc' ? 'asc' : 'desc'; }
  }
  return out;
};

const getSavedFilters = (): Record<string, any> => {
  try {
    const saved = sessionStorage.getItem(CASE_FILTERS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const Cases: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { countries } = useCountries();
  const isDocumenter = user?.role === 'DOCUMENTER';
  const isResearcher = user?.role === 'RESEARCHER';
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // URL filters win over the session-saved ones (see parseUrlFilters).
  const [saved] = useState(() => ({ ...getSavedFilters(), ...parseUrlFilters(searchParams) }));
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(saved.searchTerm ?? '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(saved.searchTerm ?? '');
  const [filterStatus, setFilterStatus] = useState<string[]>(Array.isArray(saved.filterStatus) ? saved.filterStatus : []);
  const [filterPriority, setFilterPriority] = useState(saved.filterPriority ?? 'all');
  const [filterAssignment, setFilterAssignment] = useState(saved.filterAssignment ?? 'all');
  const [filterCountries, setFilterCountries] = useState<string[]>(saved.filterCountries ?? (user?.role === 'ADMIN' ? [] : (user?.countries || [])));
  const [countryInput, setCountryInput] = useState('');
  const [filterYear, setFilterYear] = useState(saved.filterYear ?? '');
  const [filterViolationTypes, setFilterViolationTypes] = useState<string[]>(saved.filterViolationTypes ?? []);
  const [filterOtherViolationTypes, setFilterOtherViolationTypes] = useState<string[]>(saved.filterOtherViolationTypes ?? []);
  const [filterIpriRegion, setFilterIpriRegion] = useState<string[]>(saved.filterIpriRegion ?? []);
  // 'auto' = scraper-sourced (isAutoSourced), the filter the retired review queue used to hard-code.
  const [filterDataSource, setFilterDataSource] = useState<DataSourceFilter>(saved.filterDataSource ?? 'all');
  const [filterIsLDSFApplicant, setFilterIsLDSFApplicant] = useState(saved.filterIsLDSFApplicant ?? 'all');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  // Per-status totals for the status row (scoped like the list, from the dashboard stats endpoint).
  const [statusCounts, setStatusCounts] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    API.get('/api/dashboard/stats').then((r) => setStatusCounts(r.data?.perStatus ?? null)).catch(() => setStatusCounts(null));
  }, []);
  const [showArchived, setShowArchived] = useState(false); // confirmed duplicates are archived; hidden by default
  const [ipriRegionDropdownOpen, setIpriRegionDropdownOpen] = useState(false);
  const [violationTypeDropdownOpen, setViolationTypeDropdownOpen] = useState(false);
  const [otherViolationTypeDropdownOpen, setOtherViolationTypeDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(saved.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(saved.pageSize ?? 25);
  const [filterAttention, setFilterAttention] = useState<boolean>(saved.filterAttention === true);
  const [sortBy, setSortBy] = useState<SortKey>((SORT_KEYS as readonly string[]).includes(saved.sortBy) ? saved.sortBy : 'createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(saved.sortOrder === 'asc' ? 'asc' : 'desc');
  const toggleSort = (key: SortKey) => {
    if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortOrder(key === 'caseNumber' || key === 'reportedDate' || key === 'createdAt' || key === 'updatedAt' || key === 'priority' ? 'desc' : 'asc'); }
    setCurrentPage(1);
  };
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [selectedCases, setSelectedCases] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'my'>(saved.viewMode ?? (isDocumenter || isResearcher ? 'my' : 'all'));

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(CASE_FILTERS_STORAGE_KEY, JSON.stringify({
      searchTerm, filterStatus, filterPriority, filterAssignment,
      filterCountries, filterIpriRegion, filterYear, filterViolationTypes,
      filterOtherViolationTypes, filterIsLDSFApplicant, filterDataSource,
      currentPage, pageSize, viewMode, sortBy, sortOrder, filterAttention,
    }));
  }, [searchTerm, filterStatus, filterPriority, filterAssignment, filterCountries, filterIpriRegion, filterYear, filterViolationTypes, filterOtherViolationTypes, filterIsLDSFApplicant, filterDataSource, currentPage, pageSize, viewMode, sortBy, sortOrder, filterAttention]);

  // Mirror the active filters into the URL (replace, not push) so the view can be shared or
  // linked to from the dashboard. Only non-default values are written.
  useEffect(() => {
    const next = new URLSearchParams();
    filterStatus.forEach((v) => next.append('status', v));
    if (filterAssignment !== 'all') next.set('assignedTo', filterAssignment);
    filterCountries.forEach((v) => next.append('country', v));
    filterIpriRegion.forEach((v) => next.append('ipriRegion', v));
    if (debouncedSearchTerm) next.set('search', debouncedSearchTerm);
    if (filterPriority !== 'all') next.set('priority', filterPriority);
    if (filterYear) next.set('year', filterYear);
    filterViolationTypes.forEach((v) => next.append('violationType', v));
    filterOtherViolationTypes.forEach((v) => next.append('otherViolationType', v));
    if (filterDataSource !== 'all') next.set('dataSource', filterDataSource);
    if (filterIsLDSFApplicant !== 'all') next.set('ldsf', filterIsLDSFApplicant);
    if (viewMode === 'my') next.set('view', 'my');
    if (currentPage > 1) next.set('page', String(currentPage));
    if (!(sortBy === 'createdAt' && sortOrder === 'desc')) next.set('sort', `${sortBy}:${sortOrder}`);
    if (filterAttention) next.set('attention', '1');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm, filterStatus, filterPriority, filterAssignment, filterCountries, filterIpriRegion, filterYear, filterViolationTypes, filterOtherViolationTypes, filterIsLDSFApplicant, filterDataSource, viewMode, sortBy, sortOrder, filterAttention]);

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm, filterStatus, filterPriority, filterAssignment, filterCountries, filterIpriRegion, filterYear, filterViolationTypes, filterOtherViolationTypes, filterIsLDSFApplicant, filterDataSource, pageSize, viewMode, showArchived, sortBy, sortOrder, filterAttention]);

  useEffect(() => {
    // Only fetch users if user can assign cases
    if (user && ['ADMIN', 'APPROVER'].includes(user.role)) {
      fetchAvailableUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCases = async () => {
    setLoading(true);
    setSelectedCases(new Set()); // a selection never silently spans pages or filter changes
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sortBy,
        sortOrder,
        ...(filterAttention && { needsAttention: 'true' }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(filterStatus.length === 1 ? { status: filterStatus[0] } : {}),
        ...(filterStatus.length === 0 && !showArchived ? { excludeStatus: 'ARCHIVED' } : {}),
        ...(filterPriority !== 'all' && { priority: filterPriority }),
        ...(filterAssignment !== 'all' && { assignedTo: filterAssignment }),
        ...(filterYear && { year: filterYear }),
        ...(filterIsLDSFApplicant !== 'all' && { isLDSFApplicant: filterIsLDSFApplicant }),
        ...(filterDataSource === 'auto'
          ? { isAutoSourced: 'true' }
          : filterDataSource !== 'all' && { dataBasis: filterDataSource.toUpperCase() }),
        ...(viewMode === 'my' && { submittedBy: 'me' }),
        ...(viewMode === 'all' && isAdmin && { includeDrafts: 'true' }),
      });
      if (filterStatus.length > 1) filterStatus.forEach(s => params.append('status', s));
      filterCountries.forEach(c => params.append('country', c));
      filterIpriRegion.forEach(r => params.append('ipriRegion', r));
      filterViolationTypes.forEach(v => params.append('violationType', v));
      filterOtherViolationTypes.forEach(v => params.append('otherViolationType', v));

      const response = await API.get(`/api/cases?${params}`);
      setCases(response.data.cases || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // const handleStatusFilter = (status: string) => {
  //   setFilterStatus(status);
  //   setCurrentPage(1); // Reset to first page when filtering
  // };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Bulk selection functions
  const isAllSelected = cases.length > 0 && cases.every(case_ => selectedCases.has(case_.id));
  const isIndeterminate = cases.some(case_ => selectedCases.has(case_.id)) && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCases(new Set());
    } else {
      setSelectedCases(new Set(cases.map(case_ => case_.id)));
    }
  };

  const handleSelectCase = (caseId: string) => {
    const newSelected = new Set(selectedCases);
    if (newSelected.has(caseId)) {
      newSelected.delete(caseId);
    } else {
      newSelected.add(caseId);
    }
    setSelectedCases(newSelected);
  };

  const fetchAvailableUsers = async () => {
    try {
      const users = await fetchAllUsers<any>();
      // Filter to only show users who can be assigned cases
      const assignableUsers = users.filter((u: any) =>
        ['RESEARCHER', 'APPROVER', 'ADMIN'].includes(u.role)
      );
      setAvailableUsers(assignableUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Bulk reject collects a reason first (same rule as rejecting one case).
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectCategory, setBulkRejectCategory] = useState('');
  const [bulkRejectNote, setBulkRejectNote] = useState('');

  const handleBulkStatusUpdate = async (newStatus: string, rejection?: { reason: string; category: string }) => {
    if (selectedCases.size === 0) return;

    setBulkActionLoading(true);
    try {
      const caseIds = Array.from(selectedCases);
      await API.put('/api/cases/bulk/status', {
        caseIds,
        status: newStatus,
        ...(newStatus === 'REJECTED' && rejection ? { rejectionReason: rejection.reason, rejectionCategory: rejection.category || null } : {}),
      });
      setBulkRejectOpen(false);
      setBulkRejectNote('');
      setBulkRejectCategory('');

      // Refresh the cases list
      await fetchCases();
      setSelectedCases(new Set());
    } catch (error: any) {
      showToast('error', error.response?.data?.message || 'Failed to update case status');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkPriorityUpdate = async (newPriority: number) => {
    if (selectedCases.size === 0) return;

    setBulkActionLoading(true);
    try {
      const caseIds = Array.from(selectedCases);
      await API.put('/api/cases/bulk/priority', {
        caseIds,
        priority: newPriority
      });

      // Refresh the cases list
      await fetchCases();
      setSelectedCases(new Set());
    } catch (error: any) {
      showToast('error', error.response?.data?.message || 'Failed to update case priority');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkAssignment = async (assignedToId: string | null) => {
    if (selectedCases.size === 0) return;

    const assignee = assignedToId ? availableUsers.find(u => u.id === assignedToId) : null;
    const actionText = assignee
      ? `assign ${selectedCases.size} case(s) to ${assignee.firstName} ${assignee.lastName}`
      : `unassign ${selectedCases.size} case(s)`;

    const confirmed = await confirm({
      title: 'Bulk Assignment',
      message: `Are you sure you want to ${actionText}?`,
      confirmLabel: 'Confirm'
    });
    if (!confirmed) return;

    setBulkActionLoading(true);
    try {
      const caseIds = Array.from(selectedCases);
      const response = await API.put('/api/cases/bulk/assign', {
        caseIds,
        assignedToId
      });

      showToast('success', response.data.message);

      // Refresh the cases list
      await fetchCases();
      setSelectedCases(new Set());
    } catch (error: any) {
      showToast('error', error.response?.data?.message || 'Failed to assign cases');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selections when page changes
  useEffect(() => {
    setSelectedCases(new Set());
  }, [currentPage]);

  // Export handler
  const handleExport = async (filters: ExportFilters, format: 'csv' | 'excel') => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();

      // Add filters to params
      if (filters.countries && filters.countries.length > 0) {
        filters.countries.forEach(c => params.append('countries', c));
      }
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.violationTypes && filters.violationTypes.length > 0) {
        filters.violationTypes.forEach(v => params.append('violationTypes', v));
      }
      if (filters.perpetratorTypes && filters.perpetratorTypes.length > 0) {
        filters.perpetratorTypes.forEach(p => params.append('perpetratorTypes', p));
      }
      if (filters.indigenousGroups && filters.indigenousGroups.length > 0) {
        filters.indigenousGroups.forEach(g => params.append('indigenousGroups', g));
      }
      if (filters.includeVictims) params.append('includeVictims', 'true');
      if (filters.includePerpetrators) params.append('includePerpetrators', 'true');
      if (filters.includeDocuments) params.append('includeDocuments', 'true');

      const endpoint = format === 'csv' ? '/api/export/cases/csv' : '/api/export/cases/excel';
      const response = await API.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ipri-cases.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Export failed:', error);
      showToast('error', error.response?.data?.message || 'Failed to export cases');
    } finally {
      setExportLoading(false);
    }
  };

  // Check if current user can assign cases
  const isAdmin = user?.role === 'ADMIN';
  const canAssignCases = user && ['ADMIN', 'APPROVER'].includes(user.role);

  const activeFilterCount = [
    filterAttention,
    filterDataSource !== 'all',
    !!filterYear,
    filterPriority !== 'all',
    filterAssignment !== 'all',
    filterCountries.length > 0,
    filterIpriRegion.length > 0,
    filterViolationTypes.length > 0,
    filterOtherViolationTypes.length > 0,
    filterIsLDSFApplicant !== 'all',
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setFilterStatus([]);
    setFilterAttention(false);
    setFilterPriority('all');
    setFilterAssignment('all');
    setFilterCountries([]);
    setFilterIpriRegion([]);
    setFilterViolationTypes([]);
    setFilterOtherViolationTypes([]);
    setFilterIsLDSFApplicant('all');
    setCurrentPage(1);
    sessionStorage.removeItem(CASE_FILTERS_STORAGE_KEY);
  };

  const toggleStatus = (value: string) => {
    setFilterStatus(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const toggleIpriRegion = (value: string) => {
    setFilterIpriRegion(prev =>
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const toggleViolationType = (value: string) => {
    setFilterViolationTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const toggleOtherViolationType = (value: string) => {
    setFilterOtherViolationTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  // const getPriorityColor = (priority: number) => {
  //   if (priority >= 5) return 'text-red-600';
  //   if (priority >= 3) return 'text-yellow-600';
  //   return 'text-green-600';
  // };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Med-low';
      case 3: return 'Medium';
      case 4: return 'Med-high';
      case 5: return 'High';
      default: return 'Unknown';
    }
  };

  const getViolationTypeColor = (type: string) => getViolationColor(type).soft;

  const getViolationTypeLabel = (type: string) => enumLabel(type);

  return (
    <>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
      />

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={fetchCases}
      />

      {/* Bulk reject — a required reason, recorded on every selected case */}
      {bulkRejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reject {selectedCases.size} case{selectedCases.size === 1 ? '' : 's'}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {cases.filter((c) => selectedCases.has(c.id)).map((c) => c.caseNumber).join(', ')}
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label htmlFor="bulk-reject-ground" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Ground</label>
                <select id="bulk-reject-ground" value={bulkRejectCategory} onChange={(e) => setBulkRejectCategory(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                  <option value="">Select a ground…</option>
                  {REJECTION_GROUNDS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="bulk-reject-note" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Reason <span className="text-red-500">*</span></label>
                <textarea id="bulk-reject-note" value={bulkRejectNote} onChange={(e) => setBulkRejectNote(e.target.value)} rows={4} autoFocus placeholder="Explain why these cases cannot be published…" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100" />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button onClick={() => setBulkRejectOpen(false)} disabled={bulkActionLoading} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={() => handleBulkStatusUpdate('REJECTED', { reason: bulkRejectNote.trim(), category: bulkRejectCategory })} disabled={bulkActionLoading || !bulkRejectNote.trim()} className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {bulkActionLoading ? 'Rejecting…' : 'Reject cases'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFiltersModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Filters</h3>
              <button
                type="button"
                onClick={() => setShowFiltersModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Close filters"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Show archived duplicates toggle */}
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => { setShowArchived(e.target.checked); setCurrentPage(1); }}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                  Show archived duplicates
                </label>
              </div>

              {/* 2. IPRI Region */}
              <div className="relative">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><GlobeAltIcon className="h-4 w-4" />IPRI Region</label>
                <button
                  type="button"
                  onClick={() => setIpriRegionDropdownOpen(prev => !prev)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-left text-sm focus:outline-none focus:ring-primary focus:border-primary flex items-center justify-between"
                >
                  <span className={filterIpriRegion.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}>
                    {filterIpriRegion.length === 0 ? 'All Regions' : `${filterIpriRegion.length} selected`}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${ipriRegionDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {ipriRegionDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1">
                    {[
                      { value: 'LAC', label: 'LAC (Latin America & Caribbean)' },
                      { value: 'ASIA', label: 'Asia' },
                      { value: 'AFRICA', label: 'Africa' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterIpriRegion.includes(value)}
                          onChange={() => toggleIpriRegion(value)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* 3. Violation Type */}
              <div className="relative">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><ExclamationTriangleIcon className="h-4 w-4" />Violation Type</label>
                <button
                  type="button"
                  onClick={() => setViolationTypeDropdownOpen(prev => !prev)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-left text-sm focus:outline-none focus:ring-primary focus:border-primary flex items-center justify-between"
                >
                  <span className={filterViolationTypes.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}>
                    {filterViolationTypes.length === 0 ? 'All Violation Types' : `${filterViolationTypes.length} selected`}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${violationTypeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {violationTypeDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1">
                    {[
                      { value: 'KILLING', label: 'Killing' },
                      { value: 'CRIMINALIZATION', label: 'Criminalization' },
                      { value: 'OTHER', label: 'Other' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterViolationTypes.includes(value)}
                          onChange={() => toggleViolationType(value)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* 4. Other Violation Type */}
              <div className="relative">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><ShieldExclamationIcon className="h-4 w-4" />Other Violation Type</label>
                <button
                  type="button"
                  onClick={() => setOtherViolationTypeDropdownOpen(prev => !prev)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-left text-sm focus:outline-none focus:ring-primary focus:border-primary flex items-center justify-between"
                >
                  <span className={filterOtherViolationTypes.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}>
                    {filterOtherViolationTypes.length === 0 ? 'All Other Violation Types' : `${filterOtherViolationTypes.length} selected`}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${otherViolationTypeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {otherViolationTypeDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1 max-h-48 overflow-auto">
                    {[
                      { value: 'FPIC_VIOLATION', label: 'Free, Prior and Informed Consent Violation' },
                      { value: 'ARBITRARY_DETENTION', label: 'Arbitrary Detention' },
                      { value: 'KIDNAPPING', label: 'Kidnapping' },
                      { value: 'PHYSICAL_INJURY', label: 'Physical Injury' },
                      { value: 'ENFORCED_DISAPPEARANCE', label: 'Enforced Disappearance' },
                      { value: 'SEXUAL_VIOLENCE', label: 'Sexual Violence' },
                      { value: 'ONLINE_ATTACKS', label: 'Online Attacks' },
                      { value: 'FORCED_DISPLACEMENT', label: 'Forced Displacement' },
                      { value: 'LAND_GRABBING', label: 'Land Grabbing' },
                      { value: 'ENVIRONMENTAL_DESTRUCTION', label: 'Environmental Destruction' },
                      { value: 'CULTURAL_DESTRUCTION', label: 'Cultural Destruction' },
                      { value: 'THREATS_AND_INTIMIDATION', label: 'Threats And Intimidation' },
                      { value: 'VILIFICATION', label: 'Vilification' },
                      { value: 'LOSS_OF_LIVELIHOODS', label: 'Loss of Livelihoods' },
                      { value: 'DISCRIMINATION', label: 'Discrimination' },
                      { value: 'AERIAL_BOMBARDMENT', label: 'Aerial Bombing / Bombardment / Strafing' },
                      { value: 'FAILURE_TO_FULFILL_ESC_RIGHTS', label: 'Failure to Fulfill Economic, Social and Cultural Rights' },
                      { value: 'OTHER', label: 'Other' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterOtherViolationTypes.includes(value)}
                          onChange={() => toggleOtherViolationType(value)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* 5. Countries */}
              <div className="sm:col-span-2">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><MapPinIcon className="h-4 w-4" />Countries</label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Search and select countries..."
                    value={countryInput}
                    onChange={(e) => setCountryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = countryInput.trim();
                        if (val && !filterCountries.includes(val)) {
                          setFilterCountries(prev => [...prev, val]);
                          setCurrentPage(1);
                        }
                        setCountryInput('');
                      }
                    }}
                  />
                  {countryInput.trim() && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto">
                      {countries
                        .filter(c => c.toLowerCase().includes(countryInput.toLowerCase()) && !filterCountries.includes(c))
                        .slice(0, 10)
                        .map(c => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => {
                              setFilterCountries(prev => [...prev, c]);
                              setCountryInput('');
                              setCurrentPage(1);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                          >
                            {c}
                          </button>
                        ))}
                      {countries.filter(c => c.toLowerCase().includes(countryInput.toLowerCase()) && !filterCountries.includes(c)).length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching countries</div>
                      )}
                    </div>
                  )}
                </div>
                {filterCountries.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {filterCountries.map(c => (
                      <span key={c} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        {c}
                        <button
                          type="button"
                          onClick={() => { setFilterCountries(prev => prev.filter(x => x !== c)); setCurrentPage(1); }}
                          className="ml-1 text-emerald-600 hover:text-emerald-800"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Data source */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><DocumentTextIcon className="h-4 w-4" />Data source</label>
                <select
                  value={filterDataSource}
                  onChange={(e) => { setFilterDataSource(e.target.value as DataSourceFilter); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All</option>
                  <option value="primary">Primary (field documentation)</option>
                  <option value="secondary">Secondary (public reporting)</option>
                  <option value="mixed">Mixed (field documentation and public reporting)</option>
                  <option value="auto">AI-sourced (scraper)</option>
                </select>
              </div>

              {/* Year of submission */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><CheckBadgeIcon className="h-4 w-4" />Year submitted</label>
                <select
                  value={filterYear}
                  onChange={(e) => { setFilterYear(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
                >
                  <option value="">All years</option>
                  {Array.from({ length: new Date().getFullYear() - 1999 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
              </div>

              {/* 6. Priority */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><StarIcon className="h-4 w-4" />Priority</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  value={filterPriority}
                  onChange={(e) => { setFilterPriority(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Priority</option>
                  <option value="1">Low</option>
                  <option value="2">Med-low</option>
                  <option value="3">Medium</option>
                  <option value="4">Med-high</option>
                  <option value="5">High</option>
                </select>
              </div>

              {/* 7. Assignment */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><UserIcon className="h-4 w-4" />Assignment</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  value={filterAssignment}
                  onChange={(e) => { setFilterAssignment(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Assignment</option>
                  <option value="me">Assigned to me</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="assigned">Assigned</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* 8. LDSF Applicant */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><DocumentTextIcon className="h-4 w-4" />LDSF Applicant</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  value={filterIsLDSFApplicant}
                  onChange={(e) => { setFilterIsLDSFApplicant(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                type="button"
                onClick={clearAllFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setShowFiltersModal(false)}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 text-sm font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-heading">Cases</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage and review human rights violation cases
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {canAssignCases && (
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Bulk-import cases from CSV"
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                Import
              </button>
            )}
            <button
              onClick={() => setShowExportModal(true)}
              disabled={exportLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export cases to CSV or Excel"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              {exportLoading ? 'Exporting...' : 'Export'}
            </button>
            <Link
              to="/cases/new"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              New Case
            </Link>
          </div>
        </div>

      {/* Search, quick toggles, and the Filters modal */}
      <div className="mb-3 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search case number, description or tags…"
              className="pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full focus:outline-none focus:ring-primary focus:border-primary"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              aria-label="Search cases"
            />
          </div>
        </div>
        {!isDocumenter && (
          <button
            type="button"
            onClick={() => {
              const next = viewMode === 'my' ? 'all' : 'my';
              setViewMode(next);
              setCurrentPage(1);
              if (next === 'all' && !isAdmin) setFilterStatus(prev => prev.filter(st => st !== 'DRAFT'));
            }}
            aria-pressed={viewMode === 'my'}
            title="Only cases you submitted"
            className={`inline-flex items-center gap-1.5 px-4 py-2 border rounded-md text-sm font-medium ${
              viewMode === 'my'
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />
            My submissions
          </button>
        )}
        {!isDocumenter && (
          <button
            type="button"
            onClick={() => { setFilterAssignment(filterAssignment === 'me' ? 'all' : 'me'); setCurrentPage(1); }}
            aria-pressed={filterAssignment === 'me'}
            className={`inline-flex items-center gap-1.5 px-4 py-2 border rounded-md text-sm font-medium ${
              filterAssignment === 'me'
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <UserIcon className="h-4 w-4" aria-hidden="true" />
            Assigned to me
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowFiltersModal(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <FunnelIcon className="h-5 w-5 mr-2" aria-hidden="true" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Status row — the most-used filter, always visible. Multi-select; counts are overall totals. */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by status">
        <button
          type="button"
          onClick={() => { setFilterStatus([]); setCurrentPage(1); }}
          aria-pressed={filterStatus.length === 0}
          className={`px-3 py-1.5 rounded-full text-sm font-medium ${filterStatus.length === 0 ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
        >
          All
        </button>
        {[
          ...((isAdmin || viewMode === 'my') ? ['DRAFT'] : []),
          'FOR_VALIDATION', 'IN_RESEARCH', 'FOR_APPROVAL', 'APPROVED', 'REJECTED',
        ].map((value) => {
          const on = filterStatus.includes(value);
          const n = statusCounts?.[value];
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggleStatus(value)}
              aria-pressed={on}
              className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${on ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              {STATUS_LABEL[value]}
              {typeof n === 'number' && <span className={`text-xs tabular-nums ${on ? 'opacity-70' : 'text-gray-400 dark:text-gray-500'}`}>{n}</span>}
            </button>
          );
        })}
        {!isDocumenter && (
          <button
            type="button"
            onClick={() => { setFilterAttention((v) => !v); setCurrentPage(1); }}
            aria-pressed={filterAttention}
            title="Pending duplicate match, failed pipeline step, or an AI run that asked for review"
            className={`ml-auto px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 border ${filterAttention ? 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700' : 'text-amber-800 dark:text-amber-300 border-transparent hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}
          >
            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
            Needs attention
          </button>
        )}
      </div>
      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {filterStatus.map(s => (
            <FilterChip key={s} label={`Status: ${s.replace(/_/g, ' ')}`} onClear={() => { toggleStatus(s); }} />
          ))}
          {filterPriority !== 'all' && (
            <FilterChip label={`Priority: ${getPriorityLabel(parseInt(filterPriority))}`} onClear={() => { setFilterPriority('all'); setCurrentPage(1); }} />
          )}
          {filterAssignment !== 'all' && (
            <FilterChip label={`Assignment: ${filterAssignment}`} onClear={() => { setFilterAssignment('all'); setCurrentPage(1); }} />
          )}
          {filterCountries.map(c => (
            <FilterChip key={`chip-c-${c}`} label={`Country: ${c}`} onClear={() => { setFilterCountries(prev => prev.filter(x => x !== c)); setCurrentPage(1); }} />
          ))}
          {filterIpriRegion.map(r => (
            <FilterChip key={`chip-r-${r}`} label={`Region: ${r}`} onClear={() => toggleIpriRegion(r)} />
          ))}
          {filterViolationTypes.map(v => (
            <FilterChip
              key={`chip-v-${v}`}
              label={`Violation: ${getViolationTypeLabel(v)}`}
              onClear={() => toggleViolationType(v)}
            />
          ))}
          {filterOtherViolationTypes.map(v => (
            <FilterChip
              key={`chip-ov-${v}`}
              label={`Other Violation: ${getViolationTypeLabel(v)}`}
              onClear={() => toggleOtherViolationType(v)}
            />
          ))}
          {filterIsLDSFApplicant !== 'all' && (
            <FilterChip label={`LDSF Applicant: ${filterIsLDSFApplicant === 'true' ? 'Yes' : 'No'}`} onClear={() => { setFilterIsLDSFApplicant('all'); setCurrentPage(1); }} />
          )}
          {filterDataSource !== 'all' && (
            <FilterChip label={`Data: ${DATA_SOURCE_LABEL[filterDataSource]}`} onClear={() => { setFilterDataSource('all'); setCurrentPage(1); }} />
          )}
          {filterYear && (
            <FilterChip label={`Year: ${filterYear}`} onClear={() => { setFilterYear(''); setCurrentPage(1); }} />
          )}
          {filterAttention && (
            <FilterChip label="Needs attention" onClear={() => { setFilterAttention(false); setCurrentPage(1); }} />
          )}
        </div>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedCases.size > 0 && (
        <div className="mb-4 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {selectedCases.size} case{selectedCases.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedCases(new Set())}
                className="ml-4 text-sm text-gray-700 dark:text-gray-300 underline hover:no-underline"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-4">
              {/* Bulk Status Update — only transitions valid for every selected case and this role */}
              {(() => {
                const selected = cases.filter((c) => selectedCases.has(c.id));
                const role = user?.role ?? '';
                const targets = selected.length
                  ? selected.reduce<string[]>((acc, c) => acc.filter((t) => (VALID_TRANSITIONS[c.status] || []).includes(t)), VALID_TRANSITIONS[selected[0].status] || [])
                      .filter((t) => (STATUS_ROLE_PERMISSIONS[t] || ['ADMIN']).includes(role))
                  : [];
                const numbers = selected.map((c) => c.caseNumber);
                const listed = numbers.length > 8 ? `${numbers.slice(0, 8).join(', ')} and ${numbers.length - 8} more` : numbers.join(', ');
                return (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Change status to:</span>
                    {targets.length === 0 ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400 italic">No single change applies to all selected cases</span>
                    ) : (
                      <select
                        onChange={async (e) => {
                          const value = e.target.value;
                          e.target.value = '';
                          if (!value) return;
                          if (value === 'REJECTED') { setBulkRejectCategory(''); setBulkRejectNote(''); setBulkRejectOpen(true); return; }
                          const confirmed = await confirm({
                            title: `Move ${selected.length} case${selected.length === 1 ? '' : 's'} to ${STATUS_LABEL[value] || value}?`,
                            message: listed,
                            confirmLabel: 'Confirm'
                          });
                          if (confirmed) handleBulkStatusUpdate(value);
                        }}
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        disabled={bulkActionLoading}
                        aria-label="Change status of selected cases"
                      >
                        <option value="">Select status...</option>
                        {targets.map((t) => <option key={t} value={t}>{STATUS_LABEL[t] || t}</option>)}
                      </select>
                    )}
                  </div>
                );
              })()}

              {/* Bulk Priority Update */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">Change priority to:</span>
                <select
                  onChange={async (e) => {
                    const value = e.target.value;
                    e.target.value = '';
                    if (!value) return;
                    const confirmed = await confirm({
                      title: 'Bulk Priority Update',
                      message: `Are you sure you want to change ${selectedCases.size} case(s) to priority ${getPriorityLabel(parseInt(value))}?`,
                      confirmLabel: 'Confirm'
                    });
                    if (confirmed) handleBulkPriorityUpdate(parseInt(value));
                  }}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  disabled={bulkActionLoading}
                >
                  <option value="">Select priority...</option>
                  <option value="1">Low</option>
                  <option value="2">Med-low</option>
                  <option value="3">Medium</option>
                  <option value="4">Med-high</option>
                  <option value="5">High</option>
                </select>
              </div>

              {/* Bulk Assignment - Only for Admins/Managers */}
              {canAssignCases && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Assign to:</span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkAssignment(e.target.value);
                      } else {
                        handleBulkAssignment(null); // Unassign
                      }
                      e.target.value = ''; // Reset select
                    }}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    disabled={bulkActionLoading}
                  >
                    <option value="">Select assignee...</option>
                    <option value="">⚫ Unassign All</option>
                    {availableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {bulkActionLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 dark:border-gray-300"></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cases — cards below the large breakpoint, the table from lg up */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <ul className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700" aria-label="Cases">
          {loading ? (
            <li className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Loading cases...</li>
          ) : cases.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {activeFilterCount > 0 || filterStatus.length > 0 || debouncedSearchTerm ? (
                <>No cases match the current filters. <button type="button" onClick={() => { clearAllFilters(); handleSearch(''); }} className="font-medium text-gray-700 dark:text-gray-300 underline hover:no-underline">Clear filters</button></>
              ) : 'No cases yet.'}
            </li>
          ) : (
            cases.map((case_) => {
              const types = [...case_.violationTypes, ...(case_.otherViolationTypes || [])];
              const selected = selectedCases.has(case_.id);
              return (
                <li key={case_.id} className={selected ? 'bg-gray-100 dark:bg-gray-700/60' : ''}>
                  <div
                    role="link"
                    tabIndex={-1}
                    onClick={() => navigate(`/cases/${case_.id}`)}
                    className="px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleSelectCase(case_.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${case_.caseNumber}`}
                      className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-1 ring-gray-300 dark:ring-gray-600" title={DATA_BASIS_TITLE[basisFromCase(case_) || 'SECONDARY']}>{DATA_BASIS_LETTER[basisFromCase(case_) || 'SECONDARY']}</span>
                        <Link to={`/cases/${case_.id}`} onClick={(e) => e.stopPropagation()} className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:underline">{case_.caseNumber}</Link>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${case_.status === 'REJECTED' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>{STATUS_LABEL[case_.status] || case_.status}</span>
                      </div>
                      {types.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {types.map((t) => <span key={t} className={`px-1.5 py-0.5 text-[11px] rounded-full border ${getViolationTypeColor(t)}`}>{getViolationTypeLabel(t)}</span>)}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        {case_.country || '—'}{case_.region ? `, ${case_.region}` : ''}{case_.indigenousGroup ? ` · ${case_.indigenousGroup}` : ''}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>Submitted {case_.reportedDate ? formatCaseDate(case_.reportedDate) : '—'}</span>
                        <span>{case_.assignedTo ? `${case_.assignedTo.firstName} ${case_.assignedTo.lastName}` : 'Unassigned'}</span>
                        <span>{getPriorityLabel(case_.priority)} priority</span>
                        {case_.aiConfidence != null && <span title="Confidence of the latest AI validation">AI confidence {Math.round(case_.aiConfidence * 100)}%</span>}
                      </div>
                      {(case_.pendingDuplicate || (!isDecided(case_.status) && (case_.aiDecision === 'NEEDS_REVIEW' || (case_.unconfirmedAi ?? 0) > 0)) || (case_.openConflicts ?? 0) > 0) && (
                        <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
                          {case_.pendingDuplicate && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Duplicate?</span>}
                          {!isDecided(case_.status) && case_.aiDecision === 'NEEDS_REVIEW' && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">AI: review</span>}
                          {(case_.openConflicts ?? 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{case_.openConflicts} conflict{case_.openConflicts === 1 ? '' : 's'}</span>}
                          {!isDecided(case_.status) && (case_.unconfirmedAi ?? 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">{case_.unconfirmedAi} AI unchecked</span>}
                        </div>
                      )}
                    </div>
                    <Link
                      to={`/cases/${case_.id}/edit`}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Edit ${case_.caseNumber}`}
                      title="Edit"
                      className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                </li>
              );
            })
          )}
        </ul>
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-emerald-600 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50"
                />
              </th>
              <SortHeader label="Case" sortKey="caseNumber" active={sortBy} order={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Violation Types
              </th>
              <SortHeader label="IP Group" sortKey="indigenousGroup" active={sortBy} order={sortOrder} onSort={toggleSort} />
              <SortHeader label="Submitted" sortKey="reportedDate" active={sortBy} order={sortOrder} onSort={toggleSort} />
              <SortHeader label="Status" sortKey="status" active={sortBy} order={sortOrder} onSort={toggleSort} />
              <SortHeader label="Priority" sortKey="priority" active={sortBy} order={sortOrder} onSort={toggleSort} />
              <SortHeader label="Documenter" sortKey="documentorName" active={sortBy} order={sortOrder} onSort={toggleSort} />
              <SortHeader label="Assigned To" sortKey="assignee" active={sortBy} order={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <span className="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-2.5 text-center">
                  <div className="inline-flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mr-2"></div>
                    Loading cases...
                  </div>
                </td>
              </tr>
            ) : cases.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {activeFilterCount > 0 || filterStatus.length > 0 || debouncedSearchTerm ? (
                    <>
                      No cases match the current {debouncedSearchTerm ? 'search' : ''}{debouncedSearchTerm && (activeFilterCount > 0 || filterStatus.length > 0) ? ' and ' : ''}{(activeFilterCount > 0 || filterStatus.length > 0) ? `${activeFilterCount + (filterStatus.length > 0 ? 1 : 0)} filter${activeFilterCount + (filterStatus.length > 0 ? 1 : 0) === 1 ? '' : 's'}` : ''}.
                      <button type="button" onClick={() => { clearAllFilters(); handleSearch(''); }} className="ml-2 font-medium text-gray-700 dark:text-gray-300 underline hover:no-underline">Clear filters</button>
                    </>
                  ) : viewMode === 'my' ? 'You have not submitted any cases yet.' : 'No cases yet.'}
                </td>
              </tr>
            ) : (
              cases.map((case_) => (
                <tr
                  key={case_.id}
                  onClick={() => navigate(`/cases/${case_.id}`)}
                  className={`cursor-pointer ${selectedCases.has(case_.id) ? 'bg-gray-100 dark:bg-gray-700/60' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                >
                  <td className="px-4 py-2.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedCases.has(case_.id)}
                      onChange={() => handleSelectCase(case_.id)}
                      className="rounded border-gray-300 text-emerald-600 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-sm font-medium">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-1 ring-gray-300 dark:ring-gray-600" title={DATA_BASIS_TITLE[basisFromCase(case_) || 'SECONDARY']}>
                        {DATA_BASIS_LETTER[basisFromCase(case_) || 'SECONDARY']}
                      </span>
                      <Link to={`/cases/${case_.id}`} onClick={(e) => e.stopPropagation()} className="text-gray-900 dark:text-gray-100 hover:underline">
                        {case_.caseNumber}
                      </Link>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 pl-5.5">{case_.country || '—'}</p>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                    {(() => {
                      const allTypes = [
                        ...case_.violationTypes.map(t => ({ key: `v-${t}`, type: t })),
                        ...(case_.otherViolationTypes || []).map(t => ({ key: `ov-${t}`, type: t })),
                      ];
                      const visible = allTypes.slice(0, 2);
                      const remaining = allTypes.slice(2);
                      return (
                        <div className="flex items-center gap-1">
                          {visible.map(({ key, type }) => (
                            <span
                              key={key}
                              className={`px-2 py-1 text-xs rounded-lg ${getViolationTypeColor(type)}`}
                            >
                              {getViolationTypeLabel(type)}
                            </span>
                          ))}
                          {remaining.length > 0 && (
                            <span className="relative group">
                              <span className="px-2 py-1 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600">
                                +{remaining.length}
                              </span>
                              <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:flex flex-wrap gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 min-w-max">
                                {remaining.map(({ key, type }) => (
                                  <span
                                    key={key}
                                    className={`px-2 py-1 text-xs rounded-lg ${getViolationTypeColor(type)}`}
                                  >
                                    {getViolationTypeLabel(type)}
                                  </span>
                                ))}
                              </div>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 max-w-[14rem]">
                    {case_.indigenousGroup || '—'}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {case_.reportedDate ? formatCaseDate(case_.reportedDate) : '—'}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className={`inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-medium rounded-full w-fit ${case_.status === 'REJECTED' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                      {STATUS_LABEL[case_.status] || case_.status}
                    </span>
                    {(case_.pendingDuplicate || (!isDecided(case_.status) && (case_.aiDecision === 'NEEDS_REVIEW' || (case_.unconfirmedAi ?? 0) > 0)) || (case_.openConflicts ?? 0) > 0 || case_.updatedAt) && (
                      <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
                        {case_.pendingDuplicate && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="A duplicate match is awaiting review">Duplicate?</span>}
                        {!isDecided(case_.status) && case_.aiDecision === 'NEEDS_REVIEW' && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="The latest AI validation asked for review">AI: review</span>}
                        {(case_.openConflicts ?? 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Unresolved AI source conflicts">{case_.openConflicts} conflict{case_.openConflicts === 1 ? '' : 's'}</span>}
                        {!isDecided(case_.status) && (case_.unconfirmedAi ?? 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" title="AI-filled fields not yet checked against their source">{case_.unconfirmedAi} AI unchecked</span>}
                        {case_.updatedAt && <span className="text-gray-400 dark:text-gray-500" title={formatCaseDate(case_.updatedAt)}>updated {formatDistanceToNow(new Date(case_.updatedAt), { addSuffix: true })}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-medium rounded-full w-fit bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                      {getPriorityLabel(case_.priority)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {case_.isAutoSourced ? <span className="text-gray-500 dark:text-gray-400">AI-sourced</span> : case_.documentorName || '—'}
                    {case_.aiConfidence != null && (
                      <div className="text-xs text-gray-400 dark:text-gray-500" title="Confidence of the latest AI validation">
                        AI confidence {Math.round(case_.aiConfidence * 100)}%
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {case_.assignedTo ? (
                      <span className="text-gray-900 dark:text-gray-100" title={case_.assignedTo.role}>
                        {case_.assignedTo.firstName} {case_.assignedTo.lastName}
                      </span>
                    ) : (
                      <span className="text-gray-400 italic">Unassigned</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/cases/${case_.id}/edit`}
                      aria-label={`Edit ${case_.caseNumber}`}
                      title="Edit"
                      className="inline-flex items-center rounded p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {pagination.total > 0 ? (
              <>
                Showing{' '}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * pagination.limit + 1, pagination.total)}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pagination.limit, pagination.total)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.total}</span> results
              </>
            ) : (
              'No results'
            )}
          </p>
          <select
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
        {pagination.totalPages > 1 && (
          <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${
                    currentPage === 1
                      ? 'text-gray-300 cursor-not-allowed bg-gray-50 dark:bg-gray-900'
                      : 'text-gray-500 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === pagination.totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500 text-emerald-600'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span
                        key={page}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${
                    currentPage === pagination.totalPages
                      ? 'text-gray-300 cursor-not-allowed bg-gray-50 dark:bg-gray-900'
                      : 'text-gray-500 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default Cases;